import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClockWatch } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { TimePickerMenu } from 'shared/components/ui/time-picker-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { InteractionType } from 'shared/types/data-type';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { isNumber } from 'shared/utils/is-number';
import { parseDateTime } from 'shared/utils/parse-date-time';

import * as Styled from './task-meta-interaction-start-time.style';

export type TaskMetaInteractionStartTimeProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  onChange: (duration: number | null) => void;
  timeFormat: TimeFormatOptions;
  value?: number | null;
  hasIcon?: boolean;
  highlight?: boolean;
};

export const TaskMetaInteractionStartTime: React.FC<
  TaskMetaInteractionStartTimeProps
> = ({
  onChange,
  timeFormat,
  value,
  hasIcon,
  highlight,

  ...rest
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const _onChange = (time?: number) => {
    onChange(time ?? null);
    closeMenu();
  };

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        color={
          highlight && isNumber(value) ? Colors.TextPrimary : Colors.Inherit
        }
        start={hasIcon ? <Icon icon={ClockWatch} /> : undefined}
        onClick={openMenu}
        label={isNumber(value) ? parseDateTime(value!, timeFormat) : undefined}
        selected={menuOpen}
        tooltipLabel={t(
          value
            ? 'task.timeslot.start-time.tooltip.edit.label'
            : 'task.timeslot.start-time.tooltip.add.label',
        )}
        {...rest}
      />

      {menuOpen && (
        <TimePickerMenu
          referenceElement={containerRef}
          interactionType={InteractionType.Task}
          timeFormat={timeFormat}
          value={value}
          onChange={_onChange}
          onCancel={closeMenu}
          optionIcon={ClockWatch}
          placeholder={t(
            `forms.task.fields.startTime.${timeFormat === TimeFormatOptions.Military ? 'military' : 'standard'}.placeholder`,
          )}
          clearLabel={t('task.timeslot.start-time.options.clear.label')}
          hasCustomInput
          canClear
        />
      )}
    </Styled.Container>
  );
};
